import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchTesterAvgTestTime,
  fetchTesterDueThisWeek,
  fetchTesterEvidenceChecklist,
  fetchTesterMyControls,
  fetchTesterMyExceptions,
  fetchTesterMyProgress,
  fetchTesterRerunsRequired,
  fetchTesterTesters,
  type TesterAvgTestTimeResponse,
  type TesterDueThisWeekRow,
  type TesterEvidenceChecklistRow,
  type TesterMyControlsRow,
  type TesterMyExceptionRow,
  type TesterMyProgressRow,
  type TesterOption,
  type TesterRerunsRequiredRow,
} from '../../request/analytics/testerReports.ts';
import { analyticsTesterQueryKeys } from '../../queries/analytics/keys.ts';
import { useAnalyticsScope } from './useAnalyticsScope.tsx';
import { scopeKey } from '../../request/analytics/scope.ts';

/**
 * React Query v5 hooks for the Tester "my work" analytics dashboard
 * (Feature 5.3, reports #30-#36). Each report is its own independent query
 * so a single failing/slow widget doesn't block the rest of the dashboard.
 *
 * Feedback round 2026-08: every hook below now accepts an optional
 * `assigneeUserId` — when omitted, the backend scopes to the caller's own
 * assignments exactly as before; when a manager picks a tester from the
 * dashboard's "Tester" slicer (`useTesterTesters`), it's threaded through to
 * `?assignee_user_id=` on the underlying request.
 */

export function useTesterMyControls(assigneeUserId?: number | null): UseQueryResult<TesterMyControlsRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsTesterQueryKeys.myControls(assigneeUserId, scopeKey(scope)),
    queryFn: () => fetchTesterMyControls(assigneeUserId, scope),
  });
}

export function useTesterDueThisWeek(assigneeUserId?: number | null): UseQueryResult<TesterDueThisWeekRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsTesterQueryKeys.dueThisWeek(assigneeUserId, scopeKey(scope)),
    queryFn: () => fetchTesterDueThisWeek(assigneeUserId, scope),
  });
}

export function useTesterMyProgress(assigneeUserId?: number | null): UseQueryResult<TesterMyProgressRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsTesterQueryKeys.myProgress(assigneeUserId, scopeKey(scope)),
    queryFn: () => fetchTesterMyProgress(assigneeUserId, scope),
  });
}

export function useTesterMyExceptions(assigneeUserId?: number | null): UseQueryResult<TesterMyExceptionRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsTesterQueryKeys.myExceptions(assigneeUserId, scopeKey(scope)),
    queryFn: () => fetchTesterMyExceptions(assigneeUserId, scope),
  });
}

/**
 * Report #34 — Evidence Checklist. Parameterized by `runId`; the query stays
 * disabled (and simply doesn't fire) until a run id is supplied, since the
 * backend requires `run_id` as a query param and none of the other Tester
 * reports expose a run id to source one from automatically.
 */
export function useTesterEvidenceChecklist(runId: string | undefined): UseQueryResult<TesterEvidenceChecklistRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsTesterQueryKeys.evidenceChecklist(runId, scopeKey(scope)),
    queryFn: () => fetchTesterEvidenceChecklist(runId as string, scope),
    enabled: !!runId,
  });
}

export function useTesterRerunsRequired(assigneeUserId?: number | null): UseQueryResult<TesterRerunsRequiredRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsTesterQueryKeys.rerunsRequired(assigneeUserId, scopeKey(scope)),
    queryFn: () => fetchTesterRerunsRequired(assigneeUserId, scope),
  });
}

/** Report (feedback round 2026-08) — Tester Average Test Time, org-wide + per-tester breakdown. */
export function useTesterAvgTestTime(): UseQueryResult<TesterAvgTestTimeResponse, Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsTesterQueryKeys.avgTestTime(scopeKey(scope)),
    queryFn: () => fetchTesterAvgTestTime(scope),
  });
}

/** Distinct testers on current runs — feeds the dashboard's "Tester" slicer. */
export function useTesterTesters(): UseQueryResult<TesterOption[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsTesterQueryKeys.testers(scopeKey(scope)),
    queryFn: () => fetchTesterTesters(scope),
  });
}
